<template>
  <div
    v-show="visible"
    ref="popoverOverlay"
    class="mpp-overlay"
    data-testid="mag-popover"
    @click.self="$emit('close')"
  >
    <div class="mpp-window" role="dialog" aria-modal="true" :aria-label="fileName">
      <h2 class="mpp-title">{{ fileName }}</h2>
      <button type="button" class="mpp-close" aria-label="Close" @click="$emit('close')">
        <span class="material-symbols-outlined" style="font-size: 24px;">close</span>
      </button>

      <div class="mpp-preview">
        <img
          v-if="mediaType === 'image' && previewSrc"
          :src="previewSrc"
          :alt="fileName"
          class="mpp-preview-media"
        />
        <video
          v-else-if="mediaType === 'video' && previewSrc"
          :src="previewSrc"
          class="mpp-preview-media"
          controls
          playsinline
        />
        <button
          v-if="showPrev"
          type="button"
          class="mpp-nav mpp-nav--prev"
          aria-label="Previous"
          @click="$emit('prev')"
        >
          <span class="material-symbols-outlined" style="font-size: 24px;">keyboard_arrow_left</span>
        </button>
        <button
          v-if="showNext"
          type="button"
          class="mpp-nav mpp-nav--next"
          aria-label="Next"
          @click="$emit('next')"
        >
          <span class="material-symbols-outlined" style="font-size: 24px;">keyboard_arrow_right</span>
        </button>
      </div>

      <FieldRejectionFeedback
        v-if="showRejectUi"
        class="mpp-frf"
        :value="rejectReason"
        :rejected="rejected"
        :mode="mode"
        :templates="templates"
        :actor="actor"
        @input="onReasonInput"
        @update:rejected="onRejectedUpdate"
      />
    </div>
  </div>
</template>

<script>
import FieldRejectionFeedback from '@/components/FieldRejectionFeedback/FieldRejectionFeedback.vue';

export default {
  name: 'MediaPreviewPopover',
  components: {
    FieldRejectionFeedback,
  },
  props: {
    visible: { type: Boolean, default: false },
    title: { type: String, default: '' },
    mode: {
      type: String,
      default: 'admin',
      validator: (value) => ['admin', 'customer'].includes(value),
    },
    mediaType: {
      type: String,
      default: 'image',
      validator: (value) => ['image', 'video'].includes(value),
    },
    media: { type: Object, default: null },
    rejectReason: { type: String, default: '' },
    rejected: { type: Boolean, default: false },
    templates: { type: Array, default: () => [] },
    actor: { type: String, default: '' },
    /** false：仅预览（管理员直提无 AI Content 时隐藏底部驳回） */
    showRejectUi: { type: Boolean, default: true },
    showPrev: { type: Boolean, default: false },
    showNext: { type: Boolean, default: false },
  },
  computed: {
    previewSrc() {
      if (!this.media) return '';
      return this.media.previewSrc || '';
    },
    fileName() {
      return (this.media && this.media.fileName) || '';
    },
    isAdmin() {
      return this.mode === 'admin';
    },
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          if (this.$refs.popoverOverlay && this.$refs.popoverOverlay.parentNode !== document.body) {
            document.body.appendChild(this.$refs.popoverOverlay);
          }
        });
      }
    },
  },
  mounted() {
    if (this.$refs.popoverOverlay && this.$refs.popoverOverlay.parentNode !== document.body) {
      document.body.appendChild(this.$refs.popoverOverlay);
    }
  },
  beforeDestroy() {
    if (this.$refs.popoverOverlay && this.$refs.popoverOverlay.parentNode === document.body) {
      document.body.removeChild(this.$refs.popoverOverlay);
    }
  },
  methods: {
    onReasonInput(value) {
      if (!this.isAdmin) return;
      this.$emit('update:rejectReason', value);
    },
    onRejectedUpdate(value) {
      if (!this.isAdmin) return;
      this.$emit('update:rejected', value);
    },
  },
};
</script>

<style scoped lang="scss">
.mpp-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  padding: 24px;
  box-sizing: border-box;
}

.mpp-window {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  width: 100%;
  min-width: min(800px, 100%);
  max-width: 920px;
  max-height: calc(100vh - 48px);
  padding: 32px;
  border-radius: 24px;
  background: #fff;
  box-sizing: border-box;
  overflow: auto;
}

.mpp-title {
  margin: 0;
  padding-right: 40px;
  color: rgba(0, 0, 0, 0.8);
  font-size: 20px;
  font-weight: 700;
  line-height: 1.3;
}

.mpp-close {
  position: absolute;
  top: 16px;
  right: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  background: transparent;
  color: rgba(0, 0, 0, 0.45);
  cursor: pointer;

  &:hover {
    color: rgba(0, 0, 0, 0.7);
  }
}

.mpp-preview {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 240px;
  max-height: 640px;
  overflow: hidden;
  background: #f9f9f9;
  border-radius: 4px;
}

.mpp-preview-media {
  display: block;
  width: 100%;
  max-height: 640px;
  object-fit: contain;
  background: #f9f9f9;
}

.mpp-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: #fff;
  color: rgba(0, 0, 0, 0.7);
  box-shadow: 0 2px 15px rgba(183, 183, 183, 0.3);
  cursor: pointer;

  &--prev {
    left: 16px;
  }

  &--next {
    right: 16px;
  }
}

.mpp-frf {
  width: 100%;
}
</style>
